import type { TFunction } from 'i18next';

type NotifInput = {
  type?: string;
  title?: string;
  message?: string;
  data?: unknown;
};

function dataObj(data: unknown): Record<string, unknown> {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return {};
}

/** Arabic notification bodies from server — extract fields for English copy */
function tryParseArabicMessage(message: string): { key: string; params: Record<string, string> } | null {
  const m = message.trim();
  let match: RegExpMatchArray | null;

  match = m.match(/تم إضافة مزايدة جديدة على "([^"]+)" بقيمة\s*([^.\s]+)\s*د\.أ/);
  if (match) {
    return {
      key: 'notifications.parsed.newBidOnListing',
      params: { auctionTitle: match[1], amount: match[2] }
    };
  }

  match = m.match(/تم تجاوز مزايدتك بمزايدة جديدة بقيمة\s*([^.\s]+)\s*د\.أ/);
  if (match) {
    return {
      key: 'notifications.parsed.outbid',
      params: { amount: match[1] }
    };
  }

  match = m.match(/تهانينا! تم بيع سلعتك "([^"]+)" بمبلغ\s*([0-9.,]+)\s*دينار/);
  if (match) {
    return {
      key: 'notifications.parsed.itemSoldSeller',
      params: { auctionTitle: match[1], amount: match[2] }
    };
  }

  match = m.match(/مبروك! لقد فزت بسلعة "([^"]+)" مقابل\s*([0-9.,]+)\s*دينار/);
  if (match) {
    return {
      key: 'notifications.parsed.wonBuyer',
      params: { auctionTitle: match[1], amount: match[2] }
    };
  }

  match = m.match(/انتهى الوقت ولم تتم أي مزايدة على سلعتك "([^"]+)"/);
  if (match) {
    return {
      key: 'notifications.parsed.unsoldExpired',
      params: { auctionTitle: match[1] }
    };
  }

  match = m.match(/تم تسجيل تأكيد الصفقة لـ "([^"]+)"/);
  if (match) {
    return {
      key: 'notifications.parsed.dealConfirmedSeller',
      params: { auctionTitle: match[1] }
    };
  }

  match = m.match(/البائع أكّد الصفقة لـ "([^"]+)"/);
  if (match) {
    return {
      key: 'notifications.parsed.dealConfirmedBySeller',
      params: { auctionTitle: match[1] }
    };
  }

  match = m.match(/تم تسجيل استلام السلعة "([^"]+)"/);
  if (match) {
    return {
      key: 'notifications.parsed.receiptConfirmedBuyer',
      params: { auctionTitle: match[1] }
    };
  }

  match = m.match(/المشتري أكّد استلام "([^"]+)"/);
  if (match) {
    return {
      key: 'notifications.parsed.receiptConfirmedSeller',
      params: { auctionTitle: match[1] }
    };
  }

  return null;
}

const AR_TITLE_TO_KEY: Record<string, string> = {
  'مزايدة جديدة على مزادك': 'notifications.titles.newBidOnYours',
  'تم تجاوز مزايدتك': 'notifications.titles.outbid',
  'تم بيع سلعتك!': 'notifications.titles.yourItemSold',
  'لقد فزت بالمزاد!': 'notifications.titles.youWon',
  'لم يتم بيع سلعتك': 'notifications.titles.notSold',
  'تم تأكيد الصفقة بنجاح': 'notifications.titles.dealConfirmedOk',
  'تم تأكيد الصفقة من البائع': 'notifications.titles.dealConfirmedFromSeller',
  'تم تأكيد الاستلام بنجاح': 'notifications.titles.receiptOk',
  'المشتري أكّد استلام السلعة': 'notifications.titles.buyerConfirmedReceipt',
  'مزايدة جديدة': 'notifications.titles.newBid',
  'فزت بالمزاد': 'notifications.titles.youWonShort',
  'تم بيع المزاد': 'notifications.titles.auctionSold',
  'تم اعتماد المزاد': 'notifications.titles.approved',
  'تم رفض المزاد': 'notifications.titles.rejected',
  'المزاد قارب على الانتهاء': 'notifications.titles.endingSoon',
  'مزادك قارب على الانتهاء': 'notifications.titles.yourAuctionEnding'
};

const EN_TYPE_TO_TITLE: Record<string, string> = {
  NEW_BID: 'notifications.titles.newBid',
  BID_OUTBID: 'notifications.titles.outbid',
  AUCTION_ENDING: 'notifications.titles.endingSoon',
  AUCTION_ENDING_SELLER: 'notifications.titles.yourAuctionEnding',
  AUCTION_WON: 'notifications.titles.youWonShort',
  AUCTION_SOLD: 'notifications.titles.auctionSold',
  AUCTION_APPROVED: 'notifications.titles.approved',
  AUCTION_REJECTED: 'notifications.titles.rejected'
};

/**
 * Localize persisted in-app notifications for dropdown. Arabic UI keeps server text.
 */
export function localizeStoredNotification(n: NotifInput, t: TFunction, lang: string): { title: string; message: string } {
  const title = (n.title || '').trim();
  const message = (n.message || '').trim();
  const base = String(lang || 'ar').split('-')[0].toLowerCase();

  if (base === 'ar') {
    return {
      title: title || t('brand'),
      message: message || ''
    };
  }

  const data = dataObj(n.data);
  const type = String(n.type || '');

  if (type === 'NEW_BID' && data.auctionTitle != null && data.bidAmount != null) {
    return {
      title: t('notifications.titles.newBidOnYours'),
      message: t('notifications.parsed.newBidOnListing', {
        auctionTitle: String(data.auctionTitle),
        amount: String(data.bidAmount)
      })
    };
  }

  if (type === 'BID_OUTBID' && data.newBidAmount != null) {
    return {
      title: t('notifications.titles.outbid'),
      message: t('notifications.parsed.outbid', { amount: String(data.newBidAmount) })
    };
  }

  if (type === 'AUCTION_WON' && data.auctionTitle != null && data.finalPrice != null) {
    return {
      title: t('notifications.titles.youWon'),
      message: t('notifications.parsed.wonBuyer', {
        auctionTitle: String(data.auctionTitle),
        amount: String(data.finalPrice)
      })
    };
  }

  if (type === 'AUCTION_SOLD' && data.auctionTitle != null && data.finalPrice != null) {
    return {
      title: t('notifications.titles.yourItemSold'),
      message: t('notifications.parsed.itemSoldSeller', {
        auctionTitle: String(data.auctionTitle),
        amount: String(data.finalPrice)
      })
    };
  }

  if (type === 'AUCTION_APPROVED') {
    return {
      title: t('notifications.titles.approved'),
      message: data.auctionTitle != null
        ? t('notifications.parsed.listingApproved', { auctionTitle: String(data.auctionTitle) })
        : message || t('notifications.parsed.listingApprovedGeneric')
    };
  }

  if (type === 'AUCTION_REJECTED') {
    return {
      title: t('notifications.titles.rejected'),
      message: data.auctionTitle != null
        ? t('notifications.parsed.listingRejected', { auctionTitle: String(data.auctionTitle) })
        : message || t('notifications.parsed.listingRejectedGeneric')
    };
  }

  if (type === 'AUCTION_ENDING' && data.auctionTitle != null) {
    return {
      title: t('notifications.titles.endingSoon'),
      message: t('notifications.parsed.auctionEnding', { auctionTitle: String(data.auctionTitle) })
    };
  }

  if (type === 'AUCTION_ENDING_SELLER' && data.auctionTitle != null) {
    return {
      title: t('notifications.titles.yourAuctionEnding'),
      message: t('notifications.parsed.yourAuctionEnding', { auctionTitle: String(data.auctionTitle) })
    };
  }

  const parsed = tryParseArabicMessage(message);
  if (parsed) {
    const titleKey = AR_TITLE_TO_KEY[title];
    return {
      title: titleKey ? t(titleKey) : t('notifications.titles.update'),
      message: t(parsed.key, parsed.params)
    };
  }

  const titleKey = AR_TITLE_TO_KEY[title] || EN_TYPE_TO_TITLE[type];
  if (titleKey) {
    return {
      title: t(titleKey),
      message: message || t('notifications.parsed.openForDetails')
    };
  }

  return {
    title: title || t('notifications.titles.update'),
    message: message || t('notifications.parsed.openForDetails')
  };
}
